import * as Dialog from '@radix-ui/react-dialog';
import { Sparkles, UserPlus, X } from 'lucide-react';
import { useAccountGateStore, type AccountGateFeature } from '@/store/account-gate.store';
import { useGuestAuthActions } from '@/hooks/useGuestAuthActions';
import { cn } from '@/lib/cn';

/**
 * Per-feature copy for the guest → account prompt. The same modal renders
 * for every gated affordance, so call sites only pass an
 * `AccountGateFeature` and the right pitch flows automatically.
 */
const FEATURE_COPY: Record<AccountGateFeature, { title: string; subtitle: string }> = {
  save: {
    title: 'Save books & track reading',
    subtitle:
      'Create a free account to save this book to a bookshelf and start a reading plan for your reader.',
  },
  'reading-plan': {
    title: 'Start a reading plan',
    subtitle:
      'Create a free account to set a reading goal and track progress page by page.',
  },
  quiz: {
    title: 'Take the quiz',
    subtitle: 'Create a free account so your reader can take quizzes and earn coins.',
  },
  resources: {
    title: 'Unlock book resources',
    subtitle:
      'Quizzes, printable activities, vocabulary bookmarks, and Ask Ollie all live in your free account.',
  },
  reviews: {
    title: 'Reader reviews',
    subtitle:
      'Create a free account to read what other readers think and post your own review.',
  },
  'read-aloud': {
    title: 'Listen to Ollie read',
    subtitle: 'Create a free account to hear Ollie read this book aloud.',
  },
  amazon: {
    title: 'Open Amazon links',
    subtitle:
      "Create a free account to open buy links — they're protected by a parent PIN.",
  },
  'learn-with-ollie': {
    title: 'Ask Ollie about this book',
    subtitle: 'Create a free account to get kid-friendly answers about this book.',
  },
  generic: {
    title: 'Create your free OllieReads account',
    subtitle: 'Sign up to save books, track reading, take quizzes, and more.',
  },
};

/**
 * AccountGateModal — the single guest → account conversion dialog, mounted
 * once at the App root and driven by `useAccountGateStore`. The web analog
 * of the iOS `AccountGateSheet`. Chrome matches `PaywallModal` (cream→orange
 * gradient header) so it reads as a friendly part of the product. CTAs route
 * into Auth0 Universal Login; on success `ProtectedRoute` swaps the guest
 * shell for the authenticated app, so the modal tears down with it.
 */
export function AccountGateModal() {
  const open = useAccountGateStore((s) => s.open);
  const feature = useAccountGateStore((s) => s.feature);
  const closeAccountGate = useAccountGateStore((s) => s.closeAccountGate);
  const { signIn, createAccount } = useGuestAuthActions();

  const copy = FEATURE_COPY[feature ?? 'generic'];

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && closeAccountGate()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-or-slate-900/65 backdrop-blur-md data-[state=open]:animate-in data-[state=open]:fade-in data-[state=closed]:animate-out data-[state=closed]:fade-out" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-[70] w-full max-w-[480px] -translate-x-1/2 -translate-y-1/2 px-4',
            'data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:zoom-out-95',
            'focus:outline-none',
          )}
        >
          <div className="relative overflow-hidden rounded-[28px] border border-white/60 bg-white shadow-or-pop">
            <div
              className="relative px-7 pb-6 pt-9"
              style={{
                background: 'linear-gradient(135deg, #FFE9D6 0%, #FFF6EC 60%, #FCEDE2 100%)',
              }}
            >
              <Dialog.Close
                className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full text-or-orange-700/80 transition hover:bg-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-or-orange-500/40"
                aria-label="Close"
              >
                <X className="h-4 w-4" aria-hidden />
              </Dialog.Close>

              <div className="flex items-start gap-4">
                <img
                  src="/brand/ollie_logo_square.png"
                  alt=""
                  width={64}
                  height={64}
                  className="rounded-2xl shadow-or-card"
                />
                <div className="flex-1">
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-or-orange-200 bg-white/80 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-or-orange-700">
                    <Sparkles className="h-3 w-3" aria-hidden />
                    Free account
                  </div>
                  <Dialog.Title asChild>
                    <h2 className="mt-2 font-display text-[24px] font-bold leading-tight text-or-slate-900">
                      {copy.title}
                    </h2>
                  </Dialog.Title>
                </div>
              </div>
            </div>

            <div className="px-7 py-6">
              <p className="text-[14px] leading-relaxed text-or-slate-700">{copy.subtitle}</p>

              <div className="mt-6 flex flex-col gap-2.5">
                <button
                  type="button"
                  onClick={() => {
                    closeAccountGate();
                    void createAccount();
                  }}
                  className="or-gradient-btn inline-flex h-11 items-center justify-center gap-2 rounded-full px-6 text-[14px] font-semibold"
                >
                  <UserPlus className="h-4 w-4" aria-hidden />
                  Create an account
                </button>
                <button
                  type="button"
                  onClick={() => {
                    closeAccountGate();
                    void signIn();
                  }}
                  className="inline-flex h-11 items-center justify-center rounded-full border border-or-slate-200 bg-white px-6 text-[14px] font-semibold text-or-slate-700 transition hover:bg-or-slate-50"
                >
                  I already have an account
                </button>
                <button
                  type="button"
                  onClick={() => closeAccountGate()}
                  className="mt-1 text-center text-[13px] font-semibold text-or-slate-500 hover:text-or-slate-700"
                >
                  Not now
                </button>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
